/**
 * ChatList Component
 * 
 * Displays a list of all active chats for the current user.
 * Shows real-time updates, last message preview, and avatar.
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToUserChats, getUserById } from '../services/db';

const ChatList = ({ onSelectChat, selectedChatId }) => {
  const { currentUser } = useAuth();
  const [chats, setChats] = useState([]);
  const [chatUsers, setChatUsers] = useState({});
  const [loading, setLoading] = useState(true);

  // Subscribe to user's chats
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = subscribeToUserChats(currentUser.uid, async (updatedChats) => {
      setChats(updatedChats);

      // Fetch user info for each chat
      const userIds = new Set();
      updatedChats.forEach((chat) => {
        chat.members.forEach((uid) => {
          if (uid !== currentUser.uid) {
            userIds.add(uid);
          }
        });
      });

      // Fetch user profiles
      const usersData = {};
      for (const uid of userIds) {
        try {
          const user = await getUserById(uid);
          if (user) {
            usersData[uid] = user;
          }
        } catch (error) {
          console.error('Error fetching user:', error);
        }
      }

      setChatUsers(usersData);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  /**
   * Get the other user in a chat
   */
  const getOtherUser = (chat) => {
    const otherUserId = chat.members.find((uid) => uid !== currentUser.uid);
    return chatUsers[otherUserId];
  };

  /**
   * Format timestamp
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <div className="text-6xl mb-4">💬</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No chats yet</h3>
        <p className="text-gray-600">
          Search for users to start a conversation
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      {chats.map((chat) => {
        const otherUser = getOtherUser(chat);
        if (!otherUser) return null;

        const isSelected = chat.id === selectedChatId;

        return (
          <button
            key={chat.id}
            onClick={() => onSelectChat(chat.id, otherUser)}
            className={`w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
              isSelected ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
            }`}
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <img
                src={otherUser.avatarURL || '/default-avatar.png'}
                alt={otherUser.displayName}
                className="w-12 h-12 rounded-full object-cover"
              />
              {/* Online indicator (placeholder) */}
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
            </div>

            {/* Chat info */}
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900 truncate">
                  {otherUser.displayName}
                </h3>
                <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                  {formatTime(chat.lastMessageTime)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 truncate">
                  {chat.lastMessage || 'Start a conversation'}
                </p>
                {/* Unread badge (placeholder) */}
                {chat.unreadCount?.[currentUser.uid] > 0 && (
                  <span className="flex-shrink-0 ml-2 bg-primary-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {chat.unreadCount[currentUser.uid]}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500 mt-1 block">
                {otherUser.verifiedTag}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

ChatList.propTypes = {
  onSelectChat: PropTypes.func.isRequired,
  selectedChatId: PropTypes.string,
};

export default ChatList;
